import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'

// ============================================
// Global Error Handlers (catch JS errors that escape React)
// ============================================

// Track if we've shown the crash screen to avoid duplicates
let hasCrashedGlobally = false;

function showGlobalCrashScreen(error: Error | string, source?: string) {
  if (hasCrashedGlobally) return;
  hasCrashedGlobally = true;

  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorStack = error instanceof Error ? error.stack : '';
  
  console.error('🔴 Global crash detected:', errorMessage);
  console.error('🔴 Source:', source || 'unknown');
  if (errorStack) console.error('🔴 Stack:', errorStack);

  // Create crash screen DOM
  const root = document.getElementById('root');
  if (root) {
    root.innerHTML = `
      <div style="min-height:100vh;background:#0f0f0f;color:#fff;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:20px;font-family:system-ui,-apple-system,sans-serif">
        <div style="max-width:600px;width:100%;text-align:center">
          <div style="font-size:48px;margin-bottom:16px">💥</div>
          <h1 style="font-size:24px;font-weight:bold;margin-bottom:8px;color:#ef4444">แอปพลิเคชันเกิดข้อผิดพลาด</h1>
          <p style="font-size:16px;color:#9ca3af;margin-bottom:24px">JavaScript Error - ไม่สามารถเริ่มต้นแอปได้</p>
          <div style="background:#1f1f1f;border:1px solid #374151;border-radius:8px;padding:16px;margin-bottom:24px;text-align:left;overflow:auto;max-height:300px">
            <div style="font-size:12px;color:#9ca3af;margin-bottom:8px;text-transform:uppercase">Error Message</div>
            <pre style="font-size:14px;color:#f87171;white-space:pre-wrap;word-break:break-word;margin:0 0 16px 0">${errorMessage}</pre>
            ${errorStack ? `
              <div style="font-size:12px;color:#9ca3af;margin-bottom:8px;text-transform:uppercase">Stack Trace</div>
              <pre style="font-size:11px;color:#6b7280;white-space:pre-wrap;word-break:break-word;margin:0;max-height:150px;overflow:auto">${errorStack}</pre>
            ` : ''}
            ${source ? `<div style="font-size:11px;color:#4b5563;margin-top:12px">Source: ${source}</div>` : ''}
          </div>
          <div style="display:flex;gap:12px;justify-content:center;flex-wrap:wrap">
            <button onclick="location.reload()" style="background:#3b82f6;color:#fff;border:none;border-radius:6px;padding:12px 24px;font-size:14px;font-weight:500;cursor:pointer">🔄 รีโหลดหน้า</button>
            <button onclick="sessionStorage.clear();localStorage.clear();location.reload()" style="background:#374151;color:#fff;border:1px solid #4b5563;border-radius:6px;padding:12px 24px;font-size:14px;font-weight:500;cursor:pointer">🧹 ล้าง Cache</button>
          </div>
          <p style="font-size:12px;color:#6b7280;margin-top:24px">หากปัญหายังคงอยู่ กรุณาติดต่อฝ่ายสนับสนุน</p>
        </div>
      </div>
    `;
  }
}

// Catch synchronous errors
window.addEventListener('error', (event) => {
  // Ignore errors from extensions (TronLink, MetaMask, etc.)
  const isExtensionError = 
    event.filename?.includes('extension') ||
    event.filename?.includes('injected') ||
    event.filename?.includes('chrome-extension') ||
    event.filename?.includes('moz-extension');
  
  if (isExtensionError) {
    console.warn('🟡 Extension error (ignored):', event.message);
    return;
  }
  
  showGlobalCrashScreen(event.error || event.message, event.filename);
});

// Catch unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const msg = String(reason?.message || reason || '');
  
  // Ignore MetaMask/wallet related rejections
  if (msg.includes('MetaMask') || 
      msg.includes('wallet') ||
      msg.includes('provider')) {
    console.warn('🟡 Wallet rejection (ignored):', msg);
    return;
  }
  
  // Ignore camera/media constraint errors (mobile browsers)
  if (msg.includes('setPhotoOptions failed') ||
      msg.includes('OverconstrainedError') ||
      msg.includes('constraint') ||
      msg.includes('NotAllowedError') ||
      msg.includes('NotFoundError') ||
      msg.includes('NotReadableError')) {
    console.warn('🟡 Camera rejection (ignored):', msg);
    event.preventDefault();  // ป้องกัน crash screen
    return;
  }
  
  showGlobalCrashScreen(
    reason instanceof Error ? reason : new Error(String(reason)),
    'Unhandled Promise Rejection'
  );
});

console.log('🚀 main.tsx: Global error handlers registered');

// ============================================
// Service Worker Registration (deferred)
// ============================================

// Defer Service Worker registration to not block initial render
if (import.meta.env.PROD) {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      import('./utils/swRegistration').then(({ registerServiceWorker, prefetchCriticalResources }) => {
        registerServiceWorker().then((registered) => {
          if (registered) {
            console.log('🚀 Service Worker active - Network optimization enabled');
            setTimeout(() => prefetchCriticalResources(), 2000);
          }
        });
      });
    });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      import('./utils/swRegistration').then(({ registerServiceWorker, prefetchCriticalResources }) => {
        registerServiceWorker().then((registered) => {
          if (registered) {
            console.log('🚀 Service Worker active - Network optimization enabled');
            setTimeout(() => prefetchCriticalResources(), 2000);
          }
        });
      });
    }, 1);
  }
}

// ============================================
// React App Initialization
// ============================================

console.log('🚀 main.tsx: Starting React app...');

try {
  createRoot(document.getElementById("root")!).render(<App />);
  console.log('✅ main.tsx: React app rendered successfully');
} catch (error) {
  console.error('🔴 main.tsx: Failed to render React app:', error);
  showGlobalCrashScreen(error instanceof Error ? error : new Error(String(error)), 'React Initialization');
}